import type { FusionHubExtendedIntegration } from "src/common/service/fusionHub/fusionHubApi.ts";
import type { LegalEntity } from "src/common/service/nexus/endpoints/companyDomain/types.ts";
import type { Integration } from "src/common/service/nexus/endpoints/integration/types.ts";
import { isDefined } from "src/common/utils/filters/isDefined";
import { toIntegrationCompanies } from "src/features/integrations/integrationCompanies";
import { getLatestDateString } from "./getLatestDateString.js";
import type {
	DataSource,
	FusionHubDataSourceWithIntegration,
} from "src/features/integrations/overview/table/IntegrationsTableContent.js";
import type { DestinationTimestamp } from "src/common/redux/api/exopenApiTypes.ts";
import type {
	FusionHubJob,
	SourceSystemName,
} from "src/common/service/fusionHub/types.ts";

/** The job a data source's status refers to, keyed by integration id. FusionHub
 * serves these from GET /jobs rather than on the integration itself, so the
 * times are live and no status field has to be kept in step with them. */
const toCurrentJobByIntegrationId = (activeJobs: FusionHubJob[]) => {
	const byIntegrationId = new Map<number, FusionHubJob>();
	for (const job of activeJobs) {
		for (const integrationId of job.integrationIds) {
			const existing = byIntegrationId.get(integrationId);
			// WHY newest wins: a manual resync can be queued while the scheduled run it will replace is still finishing.
			if (!existing || job.queuedAt > existing.queuedAt) {
				byIntegrationId.set(integrationId, job);
			}
		}
	}
	return byIntegrationId;
};

export const mergeDataSources = ({
	nexusIntegrations,
	fusionHubIntegrations,
	legalEntities,
	destinationTimestamps,
	activeJobs,
	dummyDisplayTypes,
}: {
	fusionHubIntegrations: FusionHubExtendedIntegration[];
	nexusIntegrations: Integration[];
	legalEntities: LegalEntity[];
	destinationTimestamps: DestinationTimestamp[];
	activeJobs: FusionHubJob[];
	/** From useDummyDisplayTypes; omit where no logo or source name is shown. */
	dummyDisplayTypes?: ReadonlyMap<number, SourceSystemName>;
}): DataSource[] => {
	// O(1) lookups instead of re-scanning per fusionHub integration — domain-wide callers can have many of each.
	const nexusIntegrationBySourceId = new Map(
		nexusIntegrations
			.filter((nexusIntegration) => nexusIntegration.source === "FusionHub")
			.map((nexusIntegration) => [nexusIntegration.sourceId, nexusIntegration]),
	);
	const legalEntityById = new Map(
		legalEntities.map((legalEntity) => [legalEntity.id, legalEntity]),
	);
	const timestampByEntityId = new Map(
		destinationTimestamps.map((timestamp) => [timestamp.entityId, timestamp]),
	);
	const currentJobByIntegrationId = toCurrentJobByIntegrationId(activeJobs);

	return [
		...fusionHubIntegrations
			.map(
				({
					integration,
					currentStatus,
					lastSuccessfulSyncAt,
					name,
					organisationNumber,
					issues,
				}): FusionHubDataSourceWithIntegration | null => {
					const nexusIntegration = nexusIntegrationBySourceId.get(
						`${integration.id}`,
					);
					const currentJob = currentJobByIntegrationId.get(integration.id);

					const companies = toIntegrationCompanies(
						legalEntities,
						integration.id,
					);
					const legalEntity =
						companies.primary ??
						(nexusIntegration
							? legalEntityById.get(nexusIntegration.legalEntityId)
							: undefined);

					const fusionHubDataSource = {
						id: `${integration.id}`,
						type: "fusionhub",
						// WHY prefer legalEntity.name: FusionHub's own `name` is whatever the customer typed at onboarding (sometimes literally the source system, e.g. "AFAS") — Nexus's legal entity is the authoritative company name.
						name: legalEntity?.name ?? name,
						additionalCompanyCount: companies.additionalCount,
						companies: companies.all,
						legalEntity: legalEntity,
						sourceSystem:
							dummyDisplayTypes?.get(integration.id) ?? integration.type,
						createdAt: integration.createdAt,
						fusionHubIntegration: integration,
						lastSuccessfulSyncAt,
						currentStatus,
						currentJobQueuedAt: currentJob?.queuedAt ?? null,
						currentJobStartedAt: currentJob?.startedAt ?? null,
						organisationNumber,
						issues: issues ?? [],
						nexusStatus: nexusIntegration?.status,
						rawMartJobInProgress: false,
					} satisfies FusionHubDataSourceWithIntegration;

					return updateFusionHubDataSourceWithSyncStatus(
						fusionHubDataSource,
						timestampByEntityId,
						legalEntity,
					);
				},
			)
			.filter(isDefined),
	];
};

const updateFusionHubDataSourceWithSyncStatus = (
	dataSource: FusionHubDataSourceWithIntegration,
	timestampByEntityId: Map<string, DestinationTimestamp>,
	legalEntity: LegalEntity | undefined,
): FusionHubDataSourceWithIntegration => {
	const dlmTimestamp = timestampByEntityId.get(dataSource.id);
	const nexusTimestamp = legalEntity
		? timestampByEntityId.get(legalEntity.id)
		: undefined;

	const lastSuccessfulSyncAt = getLatestDateString(
		[
			dataSource.lastSuccessfulSyncAt,
			dlmTimestamp?.latestCurrencyManifestation,
			dlmTimestamp?.latestFinanceManifestation,
			dlmTimestamp?.latestRawManifestation,
			nexusTimestamp?.latestCurrencyManifestation,
			nexusTimestamp?.latestFinanceManifestation,
			nexusTimestamp?.latestRawManifestation,
		].filter(isDefined),
	);

	// jobInProgress (from Pumpstation's DLM) must not override an explicit FusionHub currentStatus (portal#4290).
	const isLoading =
		dataSource.currentStatus === "running" ||
		(dataSource.currentStatus === null &&
			(dlmTimestamp?.jobInProgress || nexusTimestamp?.jobInProgress));
	return {
		...dataSource,
		lastSuccessfulSyncAt,
		currentStatus: isLoading ? "running" : dataSource.currentStatus,
		rawMartJobInProgress: Boolean(
			dlmTimestamp?.jobInProgress || nexusTimestamp?.jobInProgress,
		),
	} satisfies FusionHubDataSourceWithIntegration;
};

export const scopedToLegalEntity = (
	dataSources: DataSource[],
	legalEntityId: string | undefined,
): DataSource[] =>
	legalEntityId
		? dataSources.filter((dataSource) => {
				if (!("legalEntity" in dataSource)) return false;
				const covered = dataSource.companies ?? [];
				return covered.length > 0
					? covered.some((company) => company.id === legalEntityId)
					: dataSource.legalEntity?.id === legalEntityId;
			})
		: dataSources;
